import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';
import LoadingScreen from 'src/components/LoadingScreen';
import ScrollTop from 'src/components/ScrollTop';
import { SidebarProvider } from 'src/contexts/SidebarContext';
import { getDefaultRoute } from 'src/utils/routes';
import { ROUTE_PERMISSIONS } from 'src/utils/permissions';
import { EventName } from 'src/types/amplitude';
import { chatRoutes } from 'src/components/dashboard/chat/routes';
import { User } from 'src/types/user';
import { MassiveAssignProvider } from 'src/pages/dashboard/forms/MassiveAssignContext';
import LaComerIframe from 'src/components/LaComerIframe';

const Loadable = Component =>
  function LoadableComponent(props) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ScrollTop>
          <Component {...props} />
        </ScrollTop>
      </Suspense>
    );
  };

// a function to retry loading a chunk to avoid chunk load error for out of date code
const lazyRetry = componentImport =>
  lazy(() => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem('retry-lazy-refreshed') || 'false',
    );

    // try to import the component
    try {
      return componentImport();
    } catch (error) {
      if (!hasRefreshed && error.name === 'ChunkLoadError') {
        // not been refreshed yet
        window.sessionStorage.setItem('retry-lazy-refreshed', 'true'); // we are now going to refresh
        return window.location.reload(); // refresh the page
      }
      throw error; // Default error behaviour as already tried refresh
    }
  });

// components
const AuthGuard = Loadable(
  lazyRetry(() => import('src/components/guards/AuthGuard')),
);
const DashboardLayout = Loadable(
  lazyRetry(() => import('./components/dashboard/DashboardLayout')),
);
const GuestGuard = Loadable(
  lazyRetry(() => import('src/components/guards/GuestGuard')),
);
const PermissionsGuard = Loadable(
  lazyRetry(() => import('src/components/guards/PermissionsGuard')),
);

// Authentication pages
const Login = Loadable(lazyRetry(() => import('./pages/authentication/Login')));
const LoginOTP = Loadable(
  lazyRetry(() => import('./pages/authentication/LoginOTP')),
);
const UpdatePassword = Loadable(
  lazyRetry(() => import('./pages/authentication/UpdatePassword')),
);

// Registration pages
const RegisterUser = Loadable(
  lazyRetry(() => import('./pages/registration/RegisterUser')),
);
const JoinByLink = Loadable(
  lazyRetry(() => import('./pages/registration/JoinByLink')),
);
const RestorePassword = Loadable(
  lazyRetry(() => import('./pages/registration/RestorePassword')),
);

// Error pages
const NotFound = Loadable(lazyRetry(() => import('./pages/error/NotFound')));

// Feed
const Feed = Loadable(lazyRetry(() => import('./pages/dashboard/feed/Feed')));
const PostDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/feed/PostDetail')),
);
const PostEdit = Loadable(
  lazyRetry(() => import('./pages/dashboard/feed/PostEdit')),
);
const PostInsights = Loadable(
  lazyRetry(() => import('./pages/dashboard/feed/PostInsights')),
);

const LiveStream = Loadable(
  lazyRetry(() => import('./pages/dashboard/liveStream')),
);

// Groups
const Groups = Loadable(lazyRetry(() => import('./pages/dashboard/groups')));
const Group = Loadable(
  lazyRetry(() => import('./pages/dashboard/groups/Group')),
);
const ManageGroup = Loadable(
  lazyRetry(() => import('./pages/dashboard/groups/ManageGroup')),
);
const CreateGroup = Loadable(
  lazyRetry(() => import('./pages/dashboard/groups/CreateGroup')),
);
const GroupPostDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/groups/GroupPostDetail')),
);
const GroupPostEdit = Loadable(
  lazyRetry(() => import('./pages/dashboard/groups/GroupPostEdit')),
);

// NemakEvents
const NemakEvents = Loadable(
  lazyRetry(() => import('./pages/dashboard/nemakEvents/Events')),
);
const NemakFormEvent = Loadable(
  lazyRetry(() => import('./pages/dashboard/nemakEvents/FormEvent')),
);
const NemakEvent = Loadable(
  lazyRetry(() => import('./pages/dashboard/nemakEvents/Event')),
);

// Events
const Events = Loadable(
  lazyRetry(() => import('./pages/dashboard/events/List')),
);
const NewEvent = Loadable(
  lazyRetry(() => import('./pages/dashboard/events/New')),
);

// Marketplace
const Marketplace = Loadable(
  lazyRetry(() => import('./pages/dashboard/marketplace/Marketplace')),
);
const MyMarketplacePosts = Loadable(
  lazyRetry(() => import('./pages/dashboard/marketplace/MyMarketplacePosts')),
);

// News
const News = Loadable(lazyRetry(() => import('./pages/dashboard/news/News')));
const ArticleDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/news/ArticleDetail')),
);

const Community = Loadable(
  lazyRetry(() => import('./pages/newCommunity/NewCommunity')),
);

// Profile
const Profile = Loadable(
  lazyRetry(() => import('./pages/dashboard/profile/Profile')),
);

const OrgChart = Loadable(
  lazyRetry(() => import('./pages/dashboard/orgChart/OrgChart')),
);
const OrgChartMobile = Loadable(
  lazyRetry(() => import('./pages/dashboard/orgChart/OrgChartMobile')),
);

// Tickets
const Tickets = Loadable(
  lazyRetry(() => import('./pages/dashboard/tickets/Tickets')),
);
const TicketThread = Loadable(
  lazyRetry(() => import('./pages/dashboard/tickets/TicketThread')),
);

// Chats
const Chats = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/Chats')),
);
const ChatEmpty = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/ChatEmpty')),
);
const ChatThread = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/ChatThread')),
);
const ChatNewGeneric = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/ChatNewGeneric')),
);
const ChatNewOneToOne = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/ChatNewOneToOne')),
);
const ChatNewTicket = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/ChatNewTicket')),
);
const ChatNewGroup = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/ChatNewGroup')),
);
const ChatThreadInfo = Loadable(
  lazyRetry(() => import('./pages/dashboard/chats/ChatThreadInfo')),
);

// Widgets
const Files = Loadable(
  lazyRetry(() => import('./pages/dashboard/files/Files')),
);
const People = Loadable(lazyRetry(() => import('./pages/dashboard/People')));

// Knowledge libraries
const Library = Loadable(
  lazyRetry(() => import('./pages/dashboard/libraries/Library')),
);

// Surveys
const Surveys = Loadable(
  lazyRetry(() => import('./pages/dashboard/surveys/Surveys')),
);

// Forms
const Forms = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/Forms')),
);
const FormDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/FormDetail')),
);
const FormPdf = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/FormPdf')),
);
const FormAnswer = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/FormAnswer')),
);
const FormPdfAnswer = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/FormPdfAnswer')),
);
const FormThread = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/FormThread')),
);
const FormsRequests = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/FormsRequets')),
);
const FormApprovalWorkflow = Loadable(
  lazyRetry(() => import('./pages/dashboard/forms/FormApprovalWorkflow')),
);

// Vacations
const Vacations = Loadable(
  lazyRetry(() => import('./pages/dashboard/timeOff/Vacations')),
);

const UpdateRequestByMail = Loadable(
  lazyRetry(() => import('src/pages/dashboard/timeOff/UpdateRequestByMail')),
);

// Performance
const Performance = Loadable(
  lazyRetry(() => import('./pages/dashboard/performance/Performance')),
);
const PerformanceReview = Loadable(
  lazyRetry(() => import('./pages/dashboard/performance/PerformanceReview')),
);
const PeerSelection = Loadable(
  lazyRetry(
    () => import('./pages/dashboard/performance/PerformancePeerSelection'),
  ),
);
const PerformanceReviewContainer = Loadable(
  lazyRetry(
    () => import('./pages/dashboard/performance/PerformanceReviewContainer'),
  ),
);

// Trainings
const Trainings = Loadable(
  lazyRetry(() => import('./pages/dashboard/trainings/Trainings')),
);
const Chapters = Loadable(
  lazyRetry(() => import('./pages/dashboard/trainings/Chapters')),
);
const ChapterDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/trainings/ChapterDetail')),
);
const QuizResults = Loadable(
  lazyRetry(() => import('./pages/dashboard/trainings/QuizResults')),
);
const QuizForm = Loadable(
  lazyRetry(() => import('./pages/dashboard/trainings/QuizForm')),
);

// onBoarding
const OnBoarding = Loadable(
  lazyRetry(() => import('./pages/dashboard/onboarding/Onboarding')),
);
const CategoryDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/onboarding/CategoryDetail')),
);
const TaskDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/onboarding/TaskDetail')),
);
const QuizFormOnBoarding = Loadable(
  lazyRetry(() => import('./pages/dashboard/onboarding/QuizForm')),
);
const QuizResultsOnboarding = Loadable(
  lazyRetry(() => import('./pages/dashboard/onboarding/QuizResults')),
);

// Documents
const Documents = Loadable(
  lazyRetry(() => import('./pages/dashboard/documents/Documents')),
);
const Folder = Loadable(
  lazyRetry(() => import('./pages/dashboard/documents/Folder')),
);
const SignDocument = Loadable(
  lazyRetry(() => import('./pages/dashboard/documents/SignDocument')),
);

// Acknowledgements
const Acknowledgements = Loadable(
  lazyRetry(
    () => import('./pages/dashboard/acknowledgements/Acknowledgements'),
  ),
);

// Recognitions
const Recognitions = Loadable(
  lazyRetry(() => import('./pages/dashboard/recognitions/Recognitions')),
);
const CreateIdea = Loadable(
  lazyRetry(() => import('./pages/dashboard/recognitions/CreateIdea')),
);
const Ideas = Loadable(
  lazyRetry(() => import('./pages/dashboard/recognitions/Ideas')),
);
const IdeaDetail = Loadable(
  lazyRetry(
    () => import('./pages/dashboard/recognitions/components/IdeaDetail'),
  ),
);
const Transactions = Loadable(
  lazyRetry(() => import('./pages/dashboard/recognitions/Transactions')),
);
const Products = Loadable(
  lazyRetry(() => import('./pages/dashboard/recognitions/Products')),
);

// Scorm
const ScormFolders = Loadable(
  lazyRetry(() => import('./pages/dashboard/scorm/Folders')),
);
const ScormPlayer = Loadable(
  lazyRetry(() => import('./pages/dashboard/scorm/ScormPlayer')),
);
const ScormCourses = Loadable(
  lazyRetry(() => import('./pages/dashboard/scorm/Courses')),
);

// People experience (Px)
const PxSurveysList = Loadable(
  lazyRetry(() => import('./pages/dashboard/peopleExperience/SurveysList')),
);
const PxSurvey = Loadable(
  lazyRetry(() => import('./pages/dashboard/peopleExperience/Survey')),
);
const PxSurveyResults = Loadable(
  lazyRetry(() => import('./pages/dashboard/peopleExperience/SurveyResults')),
);
const PxManagerResults = Loadable(
  lazyRetry(() => import('./pages/dashboard/peopleExperience/ManagerResults')),
);

// Goals
const Goals = Loadable(
  lazyRetry(() => import('./pages/dashboard/goals/Goals')),
);

const GoalDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/goals/components/GoalDetail')),
);

const NewGoal = Loadable(
  lazyRetry(() => import('./pages/dashboard/goals/NewGoal')),
);

// Courses
const CoursesList = Loadable(
  lazyRetry(() => import('./pages/dashboard/courses/List')),
);

const CourseDetail = Loadable(
  lazyRetry(() => import('./pages/dashboard/courses/Detail')),
);

const CourseLesson = Loadable(
  lazyRetry(() => import('./pages/dashboard/courses/Lessons')),
);

const CourseCompletion = Loadable(
  lazyRetry(() => import('./pages/dashboard/courses/Completion')),
);

// Time Tracking

const TimeTrackingPermissionsGuard = Loadable(
  lazyRetry(() => import('src/components/guards/TimeTrackingGuard')),
);

const TimeTracking = Loadable(
  lazyRetry(() => import('./pages/dashboard/timeTracking')),
);

const EmployeeTimesheet = Loadable(
  lazyRetry(() => import('./pages/dashboard/timeTracking/EmployeeTimesheet')),
);

// BanBajio
const BanBajioRequests = Loadable(
  lazyRetry(() => import('./pages/dashboard/banBajio/Requests')),
);

const BanBajioLoanRequests = Loadable(
  lazyRetry(() => import('./pages/dashboard/banBajio/LoanRequests')),
);

const BanBajioFormLoan = Loadable(
  lazyRetry(() => import('./pages/dashboard/banBajio/FormLoan')),
);

const BanBajioFormAdvancePayment = Loadable(
  lazyRetry(() => import('./pages/dashboard/banBajio/FormAdvancePayment')),
);

// Service Portal

const ServicePortal = Loadable(
  lazyRetry(
    () => import('./pages/dashboard/serviceManagement/servicePortal/Requests'),
  ),
);

const ServiceItems = Loadable(
  lazyRetry(
    () =>
      import(
        './pages/dashboard/serviceManagement/servicePortal/serviceItems/ServiceItems'
      ),
  ),
);

const ServiceItem = Loadable(
  lazyRetry(
    () =>
      import(
        './pages/dashboard/serviceManagement/servicePortal/serviceItem/ServiceItem'
      ),
  ),
);

const ServiceItemDetails = Loadable(
  lazyRetry(
    () =>
      import(
        './pages/dashboard/serviceManagement/servicePortal/requestedDetails/ServiceItemDetails'
      ),
  ),
);

// Agent Workspace

const AgentWorkspace = Loadable(
  lazyRetry(
    () => import('./pages/dashboard/serviceManagement/agentWorkspace/Tickets'),
  ),
);

const routes = (permissions: string[], user: User): RouteObject[] => {
  const dashboardRoutes = {
    path: '/',
    element: (
      <AuthGuard>
        <SidebarProvider>
          <DashboardLayout />
        </SidebarProvider>
      </AuthGuard>
    ),
    children: [
      {
        path: 'feed',
        defaultPathIf: ROUTE_PERMISSIONS.FEED,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <Feed />
              </PermissionsGuard>
            ),
          },
          {
            path: 'live-stream',
            element: (
              <PermissionsGuard
                //TODO: replace with ROUTE_PERMISSIONS.LIVESTREAM in the future
                requiredPermissions={ROUTE_PERMISSIONS.FEED}
              >
                <LiveStream />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <PostDetail />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <PostEdit />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/insights',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.POST_INSIGHTS}
              >
                <PostInsights />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'groups',
        defaultPathIf: ROUTE_PERMISSIONS.GROUPS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GROUPS}>
                <Groups />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GROUPS}>
                <Group />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/configuration',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GROUPS}>
                <ManageGroup />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/live-stream',
            element: (
              <PermissionsGuard
                //TODO: replace with ROUTE_PERMISSIONS.LIVESTREAM in the future
                requiredPermissions={ROUTE_PERMISSIONS.GROUPS}
              >
                <LiveStream />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/post/:postId',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GROUPS}>
                <GroupPostDetail />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/post/:postId/edit',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GROUPS}>
                <GroupPostEdit />
              </PermissionsGuard>
            ),
          },
          {
            path: 'create',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.CREATE_GROUPS}
              >
                <CreateGroup />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'service-portal',
        defaultPathIf: ROUTE_PERMISSIONS.WORKFLOWS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <ServicePortal />
              </PermissionsGuard>
            ),
          },
          {
            path: 'service-items',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <ServiceItems />
              </PermissionsGuard>
            ),
          },
          {
            path: 'service-items/:id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <ServiceItem />
              </PermissionsGuard>
            ),
          },
          {
            path: 'service-items/:id/detail',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <ServiceItemDetails />
              </PermissionsGuard>
            ),
          },
          {
            path: 'requests/:id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <ServiceItemDetails />
              </PermissionsGuard>
            ),
          },
          {
            path: 'service-items',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <ServiceItems />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'agent-workspace',
        defaultPathIf: ROUTE_PERMISSIONS.WORKFLOWS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <AgentWorkspace />
              </PermissionsGuard>
            ),
          },
          {
            path: 'tickets/:id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.WORKFLOWS}
              >
                <ServiceItemDetails isAgentSide={true} />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'requests-banbajio',
        defaultPathIf: ROUTE_PERMISSIONS.FEED,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <BanBajioRequests />
              </PermissionsGuard>
            ),
          },
          {
            path: 'loan',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <BanBajioLoanRequests />
              </PermissionsGuard>
            ),
          },
          {
            path: 'advancePayment',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <BanBajioFormAdvancePayment />
              </PermissionsGuard>
            ),
          },
          {
            path: 'loan/:loanType',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <BanBajioFormLoan />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'events-nemak',
        defaultPathIf: ROUTE_PERMISSIONS.EVENTS_NEMAK,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.EVENTS_NEMAK}
              >
                <NemakEvents />
              </PermissionsGuard>
            ),
          },
          {
            path: ':eventId',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.EVENTS_NEMAK}
              >
                <NemakEvent />
              </PermissionsGuard>
            ),
          },
          {
            path: ':formId/:employeeId',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.EVENTS_NEMAK}
              >
                <NemakFormEvent />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'events',
        defaultPathIf: ROUTE_PERMISSIONS.EVENTS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.EVENTS}>
                <Events />
              </PermissionsGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.EVENTS}>
                <NewEvent />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'scorm-folders',
        defaultPathIf: ROUTE_PERMISSIONS.SCORMS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SCORMS}>
                <ScormFolders />
              </PermissionsGuard>
            ),
          },
          {
            path: 'scorm-player/:courseId/:registrationId/:folderName',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SCORMS}>
                <ScormPlayer />
              </PermissionsGuard>
            ),
          },
          {
            path: 'folder/:folderId',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SCORMS}>
                <ScormCourses />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'events-nemak-mobile/:accessToken',
        defaultPathIf: ROUTE_PERMISSIONS.EVENTS_NEMAK,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.EVENTS_NEMAK}
              >
                <NemakEvents />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'recognitions-nemak',
        defaultPathIf: ROUTE_PERMISSIONS.RECOGNITIONS_NEMAK,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.RECOGNITIONS_NEMAK}
              >
                <Recognitions />
              </PermissionsGuard>
            ),
          },
          {
            path: 'createIdea',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.RECOGNITIONS_NEMAK}
              >
                <CreateIdea />
              </PermissionsGuard>
            ),
          },
          {
            path: 'ideas',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.RECOGNITIONS_NEMAK}
              >
                <Ideas />
              </PermissionsGuard>
            ),
          },
          {
            path: 'idea/:ideaId',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.RECOGNITIONS_NEMAK}
              >
                <IdeaDetail />
              </PermissionsGuard>
            ),
          },
          {
            path: 'transactions',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.RECOGNITIONS_NEMAK}
              >
                <Transactions />
              </PermissionsGuard>
            ),
          },
          {
            path: 'products',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FEED}>
                <Products />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'news',
        defaultPathIf: ROUTE_PERMISSIONS.NEWS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.NEWS}>
                <News />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.NEWS}>
                <ArticleDetail eventNameAmplitude={EventName.ARTICLE_VIEW} />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'goals',
        defaultPathIf: ROUTE_PERMISSIONS.GOALS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GOALS}>
                <Goals />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GOALS}>
                <GoalDetail />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GOALS}>
                <NewGoal />
              </PermissionsGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.GOALS}>
                <NewGoal />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'performance',
        defaultPathIf: ROUTE_PERMISSIONS.PERFORMANCE,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.PERFORMANCE}
              >
                <Performance />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.PERFORMANCE}
              >
                <PerformanceReview />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/peer',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.PERFORMANCE}
              >
                <PeerSelection />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'chats',
        defaultPathIf: ROUTE_PERMISSIONS.CHATS,
        element: (
          <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.CHATS}>
            <Chats />
          </PermissionsGuard>
        ),
        children: [
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.CHATS}>
                <ChatThread />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/info',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.CHATS}>
                <ChatThreadInfo />
              </PermissionsGuard>
            ),
          },
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.CHATS}>
                <ChatEmpty />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'topics',
        defaultPathIf: ROUTE_PERMISSIONS.CHATS,
        children: [
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.CHATS}>
                <Navigate
                  to={chatRoutes.new.ticket()}
                  replace
                />
              </PermissionsGuard>
            ),
          },
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.CHATS}>
                <Navigate
                  to={chatRoutes.new.ticket()}
                  replace
                />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'chats/new',
        defaultPathIf: ROUTE_PERMISSIONS.CHATS,
        children: [
          {
            path: 'generic',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.CHATS}>
                <ChatNewGeneric />
              </PermissionsGuard>
            ),
          },
          {
            path: 'oneToOne',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.NEW_ONE_TO_ONE}
                strict
              >
                <ChatNewOneToOne />
              </PermissionsGuard>
            ),
          },
          {
            path: 'ticket',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.NEW_TICKET}
                strict
              >
                <ChatNewTicket />
              </PermissionsGuard>
            ),
          },
          {
            path: 'group',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.NEW_GROUP}
                strict
              >
                <ChatNewGroup />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'tickets',
        element: (
          <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.TICKETS}>
            <Tickets />
          </PermissionsGuard>
        ),
        defaultPathIf: ROUTE_PERMISSIONS.TICKETS,
        children: [
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.TICKETS}>
                <TicketThread />
              </PermissionsGuard>
            ),
          },
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.TICKETS}>
                <ChatEmpty type="tickets" />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'acknowledgements',
        defaultPathIf: ROUTE_PERMISSIONS.USER_ACKNOWLEDGEMENTS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.USER_ACKNOWLEDGEMENTS}
              >
                <Acknowledgements />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'org-chart',
        defaultPathIf: ROUTE_PERMISSIONS.ORG_CHART,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.ORG_CHART}
              >
                <OrgChart />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.ORG_CHART}
              >
                <OrgChart />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'forms',
        defaultPathIf: ROUTE_PERMISSIONS.FORMS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <Forms />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <FormDetail isEdition={false} />
              </PermissionsGuard>
            ),
          },
          {
            path: 'pdf/:id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <FormPdf />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/chat',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <ChatThread />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/approval-workflow',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <FormApprovalWorkflow />
              </PermissionsGuard>
            ),
          },
          {
            path: ':templateId/completed/:answerId',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <FormAnswer />
              </PermissionsGuard>
            ),
          },
          {
            path: ':templateId/edit/:answerId',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <FormDetail isEdition />
              </PermissionsGuard>
            ),
          },
          {
            path: 'pdf/:templateId/completed/:answerId',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <FormPdfAnswer />
              </PermissionsGuard>
            ),
          },
          {
            path: 'requests',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FORMS}>
                <MassiveAssignProvider>
                  <FormsRequests />
                </MassiveAssignProvider>
              </PermissionsGuard>
            ),
            children: [
              {
                index: true,
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.FORMS}
                  >
                    <ChatEmpty type="forms" />
                  </PermissionsGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.FORMS}
                  >
                    <FormThread />
                  </PermissionsGuard>
                ),
              },
              {
                path: ':id/approval-workflow',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.FORMS}
                  >
                    <FormApprovalWorkflow />
                  </PermissionsGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'vacations',
        defaultPathIf: ROUTE_PERMISSIONS.VACATIONS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.VACATIONS}
              >
                <Vacations />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'people',
        defaultPathIf: ROUTE_PERMISSIONS.PEOPLE,
        element: (
          <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.PEOPLE}>
            <People />
          </PermissionsGuard>
        ),
      },
      {
        path: 'files',
        defaultPathIf: ROUTE_PERMISSIONS.FILES,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FILES}>
                <Files />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.FILES}>
                <Files />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'trainings',
        defaultPathIf: ROUTE_PERMISSIONS.TRAININGS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.TRAININGS}
              >
                <Trainings />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            children: [
              {
                index: true,
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.TRAININGS}
                  >
                    <Chapters />
                  </PermissionsGuard>
                ),
              },
              {
                path: 'sections/:sectionId',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.TRAININGS}
                  >
                    <ChapterDetail />
                  </PermissionsGuard>
                ),
              },
              {
                path: 'sections/:sectionId/results',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.TRAININGS}
                  >
                    <QuizResults />
                  </PermissionsGuard>
                ),
              },
              {
                path: 'sections/:sectionId/complete',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.TRAININGS}
                  >
                    <QuizForm />
                  </PermissionsGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'onboarding',
        defaultPathIf: ROUTE_PERMISSIONS.ONBOARDING,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.ONBOARDING}
              >
                <OnBoarding />
              </PermissionsGuard>
            ),
          },
          {
            path: ':categoryId',
            children: [
              {
                index: true,
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.ONBOARDING}
                  >
                    <CategoryDetail />
                  </PermissionsGuard>
                ),
              },
              {
                path: 'tasks/:taskId',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.ONBOARDING}
                  >
                    <TaskDetail />
                  </PermissionsGuard>
                ),
              },
              {
                path: 'tasks/:taskId/complete',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.ONBOARDING}
                  >
                    <QuizFormOnBoarding />
                  </PermissionsGuard>
                ),
              },
              {
                path: 'tasks/:taskId/results',
                element: (
                  <PermissionsGuard
                    requiredPermissions={ROUTE_PERMISSIONS.ONBOARDING}
                  >
                    <QuizResultsOnboarding />
                  </PermissionsGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'surveys',
        defaultPathIf: ROUTE_PERMISSIONS.SURVEYS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SURVEYS}>
                <Surveys />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SURVEYS}>
                <FormDetail isEdition={false} />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/chat',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SURVEYS}>
                <ChatThread />
              </PermissionsGuard>
            ),
          },
          {
            path: ':templateId/completed/:answerId',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SURVEYS}>
                <FormAnswer />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'documents',
        defaultPathIf: ROUTE_PERMISSIONS.DOCUMENTS,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.DOCUMENTS}
              >
                <Documents />
              </PermissionsGuard>
            ),
          },
          {
            path: ':folderId',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.DOCUMENTS}
              >
                <Folder />
              </PermissionsGuard>
            ),
          },
          {
            path: 'pending/files/:id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.DOCUMENTS}
              >
                <SignDocument />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'courses',
        defaultPathIf: ROUTE_PERMISSIONS.COURSES,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.COURSES}>
                <CoursesList />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.COURSES}>
                <CourseDetail />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/lessons/:lessonId',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.COURSES}>
                <CourseLesson />
              </PermissionsGuard>
            ),
          },
          {
            path: ':id/completion',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.COURSES}>
                <CourseCompletion />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'marketplace',
        defaultPathIf: ROUTE_PERMISSIONS.MARKETPLACE,
        children: [
          {
            index: true,
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.MARKETPLACE}
              >
                <Marketplace />
              </PermissionsGuard>
            ),
          },
          {
            path: 'mine',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.MARKETPLACE}
              >
                <MyMarketplacePosts />
              </PermissionsGuard>
            ),
          },
        ],
      },
      // Low priority: no tienen una route root y requieren de un id.
      {
        path: 'profile',
        defaultPathIf: ROUTE_PERMISSIONS.PROFILE,
        children: [
          {
            path: ':id',
            element: (
              <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.PROFILE}>
                <Profile />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'library',
        defaultPathIf: ROUTE_PERMISSIONS.KNOWLEDGE_LIBRARY,
        children: [
          {
            path: ':id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.KNOWLEDGE_LIBRARY}
              >
                <Library />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'people-experience',
        defaultPathIf: ROUTE_PERMISSIONS.PEOPLE_EXPERIENCE,
        children: [
          {
            path: 'surveys',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.PEOPLE_EXPERIENCE}
              >
                <PxSurveysList />
              </PermissionsGuard>
            ),
          },
          {
            path: 'surveys/manager',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.PEOPLE_EXPERIENCE}
              >
                <PxSurveyResults />
              </PermissionsGuard>
            ),
          },
          {
            path: 'surveys/:id',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.PEOPLE_EXPERIENCE}
              >
                <PxSurvey />
              </PermissionsGuard>
            ),
          },
          {
            path: 'surveys/:id/manager-results',
            element: (
              <PermissionsGuard
                requiredPermissions={ROUTE_PERMISSIONS.PEOPLE_EXPERIENCE}
              >
                <PxManagerResults />
              </PermissionsGuard>
            ),
          },
        ],
      },
      {
        path: 'time-tracking',
        defaultPathIf: ROUTE_PERMISSIONS.TIME_TRACKING,
        children: [
          {
            index: true,
            element: (
              <TimeTrackingPermissionsGuard>
                <TimeTracking />
              </TimeTrackingPermissionsGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <TimeTrackingPermissionsGuard>
                <EmployeeTimesheet />
              </TimeTrackingPermissionsGuard>
            ),
          },
        ],
      },
    ],
  };

  dashboardRoutes.children.push(
    getDefaultRoute(dashboardRoutes, permissions, user),
  );

  const authenticationRoutes = {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: 'otp',
        element: (
          <GuestGuard>
            <LoginOTP />
          </GuestGuard>
        ),
      },
      {
        path: 'update-password',
        element: (
          <GuestGuard>
            <UpdatePassword />
          </GuestGuard>
        ),
      },
    ],
  };

  const registrationRoutes = [
    {
      path: 'register-user',
      element: (
        <GuestGuard>
          <RegisterUser />
        </GuestGuard>
      ),
    },
    {
      path: 'join-by-link',
      element: (
        <GuestGuard>
          <JoinByLink />
        </GuestGuard>
      ),
    },
    {
      path: 'restore-password',
      element: (
        <GuestGuard>
          <RestorePassword />
        </GuestGuard>
      ),
    },
    {
      path: 'blank.html',
      element: <></>,
    },
  ];

  const externalReviewRoutes = {
    path: 'performance/:id/external',
    element: (
      <GuestGuard>
        <PerformanceReviewContainer />
      </GuestGuard>
    ),
  };

  const timeoffUpdateRequest = {
    path: 'time-off-update-request',
    element: <UpdateRequestByMail />,
  };

  const communityRoutes = {
    path: 'new',
    element: (
      <GuestGuard>
        <Community />
      </GuestGuard>
    ),
  };

  const orgChartMobile = {
    path: 'org-chart-mobile/:accessToken/:id',
    element: (
      <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.ORG_CHART}>
        <OrgChartMobile />
      </PermissionsGuard>
    ),
  };

  const scormMobile = {
    path: '/scorm-courses-mobile/:accessToken',
    element: (
      <PermissionsGuard requiredPermissions={ROUTE_PERMISSIONS.SCORMS}>
        <ScormFolders isMobile={true} />
      </PermissionsGuard>
    ),
  };

  const nemakRecognitionsMobileRoutes = {
    path: '/recognitions-nemak-mobile/:accessToken',
    element: (
      <PermissionsGuard
        requiredPermissions={ROUTE_PERMISSIONS.RECOGNITIONS_NEMAK}
      >
        <Recognitions isMobile={true} />
      </PermissionsGuard>
    ),
  };

  // Temporary route for the LaComer team to adapt their webpage
  const lacomerPocRoutes = {
    path: 'lacomer-poc',
    defaultPathIf: false,
    element: <LaComerIframe />,
  };

  const errorRoutes = {
    path: '*',
    children: [
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  };

  return [
    authenticationRoutes,
    communityRoutes,
    timeoffUpdateRequest,
    scormMobile,
    nemakRecognitionsMobileRoutes,
    orgChartMobile,
    ...registrationRoutes,
    externalReviewRoutes,
    dashboardRoutes,
    errorRoutes,
    lacomerPocRoutes,
  ];
};

export default routes;
